// @givmo/app-admin — Dashboard (employer-admin home).
//
// LIVE. This file is the COMPOSITION SHELL: it owns the chart/panel queries and renders one component
// per section from `./sections/`. The sections are purely presentational — they receive resolved props
// and hold no hooks — with one exception that reads its own signals: <NeedsAttention> (join requests +
// wallet).
//
// Reads driving the screen:
//   - companiesGetCompanyDashboard         → money strip · Participation · Impact ·
//                                            (via `useMatchApprovals`) the approval rows
//   - companiesGetCompanyDonationVolume    → giving-over-time (matched + company-gave, vs prior year)
//   - companiesGetCompanyTopCharities      → the ranked Impact panel
//   - companiesGetCompanyWallet            → the wallet-balance card (EP-WEB9). The SAME query key
//                                            <NeedsAttention> reads, so this is a cache hit rather than
//                                            a second request — and the card and the low-balance flag
//                                            can never quote different numbers.
//   - companiesGetCompanyMatchUtilization  → the utilization card (EP-WEB9; enforcement basis)
//   - companiesGetCompany                  → the DECLARED headcount, the enrollment-rate denominator
//   - the invites list (paged to exhaustion) → the pending-invite count on Participation (EP-WEB8)
//
// ★ THE APPROVAL QUEUE MOVED TO `/approvals` (EP-DES1 §1). The dashboard keeps a "Needs attention"
// SUMMARY carrying the pending count, the total dollars, the top rows with their real Approve/Decline
// actions, and Approve-all — so the most common action is still one click from the home screen — while
// the queue itself (search, filters, decided history, pagination, grouped-row disclosure) lives on its
// own destination. Both surfaces drive the SAME money path through `useMatchApprovals`, which owns the
// reads, the Owner gate, the decline mutation, and renders <ApproveMatchDialog> + <ConfirmDialog>. Every
// charge-safety guard (idempotency, no-blind-retry on maybe-charged, single-flight,
// confirm-before-charge, card-required, budget pre-check) stays inside the dialog, untouched by the
// split — a layout change is never a money change.
//
// The money strip's "Add funds" asks `mayAddFunds` over the same standing read (EP-SUITE7, D-EP-50):
// the base-role Owner with its fail-OPEN, or a holder of the deposit's own string — a Donations Owner
// who is not a Global Admin among them. The same predicate gates the deposit on both hosts' wallet
// pages, so the link always lands on a page that offers it.
//
// ★ THE "COMPANY DONATIONS" TABLE MOVED TO `/give` (EP-WEB14, EP-DES1 §2). The company's own DIRECT
// donations (the `donation_match: null` rows this read UNIONs into `donation_matching_activity` —
// CAM-BE4/BE7) are the giving HISTORY that belongs beside the flow that creates them, so `/give` is now
// compose-plus-ledger and fetches this same dashboard read for its rows. What stays here is the money
// strip's `stat-company-gave` figure — a different testid and a different job: the headline number
// belongs on the home screen, the itemization does not. Either way those rows never enter the approvals
// machinery: not counted as pending, never in the Approve-all set.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyDashboardOptions,
  companiesGetCompanyDonationVolumeOptions,
  companiesGetCompanyMatchUtilizationOptions,
  companiesGetCompanyOptions,
  companiesGetCompanyTopCharitiesOptions,
  companiesGetCompanyWalletOptions,
} from '@givmo/api-client/query';
import { testIdProp } from '@givmo/design-system';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { useMatchApprovals } from '../approvals/use-match-approvals';
import { mayAddFunds } from '../settings/funding-permissions';
import { holdsPrivacySettings } from '../settings/privacy-visibility-card';
import { toEnrollmentRate } from '../employees/invites-decode';
import { useInviteCounts } from '../employees/use-invite-counts';
import { toWalletFunding } from './dashboard-decode';
import { toUtilizationView } from './utilization-decode';
import { DashboardHeader } from './sections/dashboard-header';
import { NeedsAttention } from './sections/needs-attention';
import { ParticipationPanel } from './sections/participation-panel';
import { StatStrip } from './sections/stat-strip';
import { TopCharitiesCard } from './sections/top-charities-card';
import { VolumeCard } from './sections/volume-card';

export function DashboardPage() {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const dashboard = useQuery(companiesGetCompanyDashboardOptions());
  const volume = useQuery(companiesGetCompanyDonationVolumeOptions());
  const topCharities = useQuery(companiesGetCompanyTopCharitiesOptions({ query: { limit: 5 } }));

  // The shared approval engine — same hook, same cache entry, same dialogs as `/approvals`.
  const approvals = useMatchApprovals();

  // The wallet balance behind the money strip's first card. Reuses the charge dialog's spendable-balance
  // precedence (available_balance, else balance) so the card and the "Pay with" option agree.
  const walletQuery = useQuery(companiesGetCompanyWalletOptions());
  const walletFunding = useMemo(() => toWalletFunding(walletQuery.data), [walletQuery.data]);

  // Program utilization (EP-WEB9) — its own read on the budget-ENFORCEMENT basis, deliberately not
  // derived from the dashboard's display figures.
  const utilizationQuery = useQuery(companiesGetCompanyMatchUtilizationOptions());
  const utilization = useMemo(
    () => toUtilizationView(utilizationQuery.data),
    [utilizationQuery.data],
  );

  // `GET /admin/company` carries the ADMIN-DECLARED headcount that is the enrollment-rate denominator.
  const company = useQuery(companiesGetCompanyOptions());

  // The contextual door onto the Company visibility settings (EP-MNT5(c), D-EP-48 ruling 3)
  // — where this host has one (the console; the portal keeps its Privacy tab), and only for a
  // holder of the settings' write string: the settings page draws the card for no one else, so for
  // anyone else the link would open a page without it. The standing read is the shell's cache entry.
  const standing = useQuery(companiesGetCompanyAdminOptions());
  const privacyDoor =
    paths.privacy !== null && holdsPrivacySettings(standing.data?.permissions)
      ? paths.privacy
      : null;
  const canAddFunds = mayAddFunds({
    settled: standing.isSuccess,
    role: standing.data?.admin_company_role,
    permissions: standing.data?.permissions,
  });

  const data = dashboard.data;

  const enrollment = useMemo(
    () => toEnrollmentRate(data?.active_employees, company.data?.number_of_employees),
    [data?.active_employees, company.data?.number_of_employees],
  );

  // The pending-invite figure for the Participation panel (EP-WEB8), now a single cheap read of
  // `GET /admin/company/invites/counts` (EP-BE12) instead of paging every INVITED row on every dashboard
  // load. `useInviteCounts` owns the fallback for a backend that predates that route. Its own query, so
  // a failure here degrades to "no number shown" rather than taking the panel's other figures down —
  // and null, never 0, because "0 pending" when we could not count is a claim the client cannot make.
  const { counts: inviteTotals } = useInviteCounts();
  const pendingInvites = inviteTotals?.invited ?? null;

  const points = volume.data?.donation_volume_series ?? [];
  const topCharityRows = topCharities.data?.top_charities_ytd ?? [];

  const goToInvites = () => void navigate({ to: paths.employees, search: { tab: 'invites' } });
  // Deposits live where this host keeps the wallet — the portal's Settings → Payment Methods (its
  // `?tab=` deep link), the console's Organization › Wallet (EP-MNT4's address book); the match
  // budget lives on its own page since EP-MNT2. Each door lands on the exact surface its label names.
  const goToPaymentMethods = () =>
    void navigate({ to: paths.paymentMethods.to, search: paths.paymentMethods.search });
  const goToMatchProgram = () => void navigate({ to: paths.matchProgram });

  return (
    <div
      className="mx-auto flex w-full max-w-[1280px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-dashboard')}
    >
      <DashboardHeader />

      {/* Block 1 (spec §3): above everything, and it renders NOTHING when there is nothing to do. */}
      <NeedsAttention
        rows={approvals.rows}
        pendingCount={approvals.pendingCount}
        approvalTotal={approvals.approvalTotal}
        canManage={approvals.canManage}
        isOwnerConfirmed={approvals.isOwnerConfirmed}
        canApproveAll={approvals.canApproveAll}
        rejectingId={approvals.rejectingId}
        onApprove={approvals.openApprove}
        onDecline={approvals.openDecline}
        onApproveAll={approvals.openApproveAll}
      />

      {/* Block 2 (spec §127): the money strip. */}
      <StatStrip
        data={data}
        loading={dashboard.isPending}
        isError={dashboard.isError}
        error={dashboard.error}
        onRetry={() => void dashboard.refetch()}
        walletBalance={walletFunding?.balanceDisplay ?? null}
        walletLoading={walletQuery.isPending}
        canManage={approvals.canManage}
        canAddFunds={canAddFunds}
        onAddFunds={goToPaymentMethods}
        utilization={utilization}
        utilizationLoading={utilizationQuery.isPending}
        utilizationIsError={utilizationQuery.isError}
        utilizationError={utilizationQuery.error}
        onUtilizationRetry={() => void utilizationQuery.refetch()}
        onSetBudget={goToMatchProgram}
        onInviteEmployees={goToInvites}
      />

      {/* Block 3 (spec §129): enrollment rate, active employees, pending invites, median match. */}
      <ParticipationPanel
        data={data}
        loading={dashboard.isPending}
        isError={dashboard.isError}
        error={dashboard.error}
        onRetry={() => void dashboard.refetch()}
        enrollment={enrollment}
        enrollmentLoading={company.isPending}
        enrollmentIsError={company.isError}
        onEnrollmentRetry={() => void company.refetch()}
        pendingInvites={pendingInvites}
        onManageInvites={goToInvites}
        words={paths.words}
      />

      {/* Blocks 4 + 5 (spec §131 / §133): giving over time, and the Impact panel. */}
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-3">
        <VolumeCard
          points={points}
          loading={volume.isPending}
          isError={volume.isError}
          error={volume.error}
          onRetry={() => void volume.refetch()}
        />
        <TopCharitiesCard
          rows={topCharityRows}
          loading={topCharities.isPending}
          isError={topCharities.isError}
          error={topCharities.error}
          onRetry={() => void topCharities.refetch()}
          charitiesSupported={data?.charities_supported_ytd}
          onOpenRecap={() => void navigate({ to: paths.reports })}
          privacyDoor={privacyDoor}
        />
      </div>

      {/* ★ The approve/charge dialog + the decline confirm, owned by the shared hook. The summary's
          buttons open nothing without this. */}
      {approvals.dialogs}
    </div>
  );
}
